/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// A form printed onto the organisation's own paper (PaperLayer.cs in the jobs
// service): reading the paper the administrator picks, what each field can put
// on it, and values to print a test copy with.

import {
  allFields,
  FormDefinition,
  FormField,
  FormPlace,
  FormPlacePart,
  FormValue,
  FormValues,
  hasOptions,
  paperRows,
  printedType,
  takesValue
} from './forms.model';

/** PaperLayer.MaxPages and MaxBytes. */
export const MAX_PAPER_PAGES = 20;
export const MAX_PAPER_BYTES = 20 * 1024 * 1024;
/** The sizes a place's text can be; the service makes it smaller when it would not fit. */
export const MIN_PLACE_SIZE = 6;
export const MAX_PLACE_SIZE = 36;
export const PLACE_SIZE = 12;

export type PaperProblem = 'type' | 'size' | 'pages' | 'unreadable';

/** Why a picked file cannot be the paper. */
export class PaperError extends Error {
  constructor(readonly problem: PaperProblem) {
    super(`The paper cannot be used: ${problem}`);
  }
}

/** A paper picked by the administrator, as a PDF with its pages measured the way pdf.js shows them. */
export interface PickedPaper {
  pdf: Blob;
  pages: { width: number; height: number }[];
}

export const isPaperFile = (file: File): boolean => /\.(pdf|png|jpe?g)$/i.test(file.name) || /^(application\/pdf|image\/(png|jpeg))$/.test(file.type);

/** Reads a PDF, or a photo or scan of the paper (made into a one-page PDF), and measures its pages. Rejects with a PaperError. */
export async function readPaper(file: File): Promise<PickedPaper> {
  if (!isPaperFile(file)) {
    throw new PaperError('type');
  }
  if (file.size > MAX_PAPER_BYTES) {
    throw new PaperError('size');
  }
  const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdf = isPdf ? bytes : await imageToPdf(bytes, /\.png$/i.test(file.name) || file.type === 'image/png');
  const pages = await measure(pdf.slice());
  if (pages.length === 0 || pages.length > MAX_PAPER_PAGES) {
    throw new PaperError('pages');
  }
  if (pdf.byteLength > MAX_PAPER_BYTES) {
    throw new PaperError('size');
  }
  return { pdf: new Blob([pdf], { type: 'application/pdf' }), pages };
}

/** A photo or scan on an A4 page turned the way the picture is, filling it. */
async function imageToPdf(bytes: Uint8Array, png: boolean): Promise<Uint8Array> {
  const { PDFDocument } = await import('pdf-lib');
  const doc = await PDFDocument.create();
  let image;
  try {
    image = png ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
  } catch {
    throw new PaperError('unreadable');
  }
  const [short, long] = [595.28, 841.89];
  const [width, height] = image.width > image.height ? [long, short] : [short, long];
  const page = doc.addPage([width, height]);
  const scale = Math.min(width / image.width, height / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  page.drawImage(image, { x: (width - w) / 2, y: (height - h) / 2, width: w, height: h });
  return doc.save();
}

/** Each page as pdf.js shows it at scale 1: its crop box in points, turned the way it reads. */
export async function measure(pdf: Uint8Array): Promise<{ width: number; height: number }[]> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;
  let doc;
  try {
    doc = await pdfjs.getDocument({ data: pdf }).promise;
  } catch {
    // a password, or not a PDF at all
    throw new PaperError('unreadable');
  }
  try {
    if (doc.numPages > MAX_PAPER_PAGES) {
      throw new PaperError('pages');
    }
    const pages: { width: number; height: number }[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      const viewport = (await doc.getPage(n)).getViewport({ scale: 1 });
      pages.push({ width: round(viewport.width), height: round(viewport.height) });
    }
    return pages;
  } finally {
    void doc.destroy();
  }
}

const round = (n: number): number => Math.round(n * 100) / 100;

// ── what goes on the paper ─────────────────────────────────────────────

/** Something a field (or the form itself) can put on the paper. */
export interface PlacePart {
  part: FormPlacePart;
  option?: string;
}

/** The parts a field can place: its value, and for some types a part of it or a tick for each choice. */
export function partsOf(field: FormField): PlacePart[] {
  if (!takesValue(field)) {
    return [];
  }
  const parts: PlacePart[] = [{ part: 'value' }];
  if (printedType(field) === 'money') {
    parts.push({ part: 'words' });
  }
  if (field.type === 'date') {
    parts.push({ part: 'day' }, { part: 'month' }, { part: 'year' });
  }
  if (hasOptions(field.type)) {
    parts.push(...(field.options ?? []).filter((o) => o.trim()).map((option) => ({ part: 'option' as const, option })));
  }
  return parts;
}

/** What the service knows of each copy, for the paper's own blanks. */
export const MARK_PARTS: PlacePart[] = [
  { part: 'reference' },
  { part: 'name' },
  { part: 'date' },
  { part: 'day' },
  { part: 'month' },
  { part: 'year' }
];

export const samePart = (place: FormPlace, part: PlacePart): boolean =>
  (place.part ?? 'value') === part.part && (part.part !== 'option' || place.option === part.option);

/** A new place for a part, centred on a point of the page, sized for what usually goes there. */
export function newPlace(field: FormField | null, part: PlacePart, page: number, centre: { x: number; y: number }): FormPlace {
  const [w, h] = defaultSize(field, part);
  const place: FormPlace = { page, x: round(Math.max(0, centre.x - w / 2)), y: round(Math.max(0, centre.y - h / 2)), w, h };
  if (part.part !== 'value') {
    place.part = part.part;
  }
  if (part.option !== undefined) {
    place.option = part.option;
  }
  if (field?.type === 'thaiId' && part.part === 'value') {
    place.cells = 13;
  }
  if (field?.type === 'table') {
    place.rowHeight = 18;
  }
  return place;
}

function defaultSize(field: FormField | null, part: PlacePart): [number, number] {
  switch (part.part) {
    case 'option':
      return [12, 12];
    case 'day':
      return [30, 18];
    case 'month':
      return [80, 18];
    case 'year':
      return [45, 18];
    case 'words':
      return [260, 18];
    case 'reference':
      return [150, 18];
  }
  switch (field?.type) {
    case 'checkbox':
      return [12, 12];
    case 'textarea':
      return [320, 60];
    case 'address':
      return [320, 40];
    case 'table':
      return [460, 90];
    case 'signature':
      return [150, 40];
    case 'thaiId':
      return [210, 20];
    default:
      return [180, 18];
  }
}

/** How many places a form's fields and marks have on the paper. */
export const placeCount = (form: FormDefinition): number =>
  (form.paper?.marks?.length ?? 0) + allFields(form).reduce((n, f) => n + (f.places?.length ?? 0), 0);

// ── a test copy ────────────────────────────────────────────────────────

/** Values that fill every place, so a test print shows where each will land on the paper. */
export function sampleValues(form: FormDefinition, today = new Date()): FormValues {
  const values: FormValues = {};
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  for (const field of allFields(form)) {
    const value = sample(form, field, iso);
    if (value !== null) {
      values[field.key] = value;
    }
  }
  return values;
}

function sample(form: FormDefinition, field: FormField, today: string): FormValue | null {
  const first = (field.options ?? []).find((o) => o.trim()) ?? '';
  switch (field.type) {
    case 'number':
      return 12;
    case 'money':
      return 1234.5;
    case 'date':
      return today;
    case 'select':
    case 'radio':
      return first;
    case 'checkboxes':
      return first ? [first] : [];
    case 'checkbox':
      return true;
    case 'email':
      return 'somchai@example.com';
    case 'phone':
      return '081-234-5678';
    case 'thaiId':
      return '1101200001238';
    case 'textarea':
      return `${field.label} ตัวอย่างข้อความหลายบรรทัด\nบรรทัดที่สอง`;
    case 'address':
      return { line: '99/9 ถนนตัวอย่าง', subdistrict: 'ลุมพินี', district: 'ปทุมวัน', province: 'กรุงเทพมหานคร', postcode: '10330' };
    case 'table': {
      const rows = Math.min(paperRows(form, field) ?? 3, 30);
      return Array.from({ length: rows }, (_, i) =>
        Object.fromEntries(
          (field.columns ?? [])
            .filter((c) => c.type !== 'calc')
            .map((c) => [c.key, c.type === 'text' ? `${c.label || 'รายการ'} ${i + 1}` : c.type === 'date' ? today : (i + 1) * 100])
        )
      );
    }
    case 'calc':
    case 'paragraph':
    case 'signature':
      return null;
    default:
      return field.label || 'ตัวอย่าง';
  }
}
